import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../../components/Shared/Button';
import { Badge } from '../../components/Shared/Badge';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '../../components/Shared/Table';
import { GripVertical, Pencil, Trash2, Eye } from 'lucide-react';
import type { FacilityFlowCoreModulesCustomersDtosObjectDto } from '../../api/generated/models';

interface SortableObjectRowProps {
  object: FacilityFlowCoreModulesCustomersDtosObjectDto;
  onEdit: (object: FacilityFlowCoreModulesCustomersDtosObjectDto) => void;
  onDelete: (id: string) => void;
}

function SortableObjectRow({ object, onEdit, onDelete }: SortableObjectRowProps) {
  const navigate = useNavigate();
  const { t } = useTranslation(['customers', 'common']);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: object.id || '' });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const goToDetail = () => {
    if (!object.id) return;
    navigate(`/objects/${object.id}`);
  };

  return (
    <TableRow ref={setNodeRef} style={style} onClick={goToDetail}>
      <TableCell className="hidden sm:table-cell w-10">
        <div
          className="cursor-grab active:cursor-grabbing p-1 text-slate-400 hover:text-slate-700"
          onClick={(e) => e.stopPropagation()}
          title={t('customers:objects.actions.reorder')}
          aria-label={t('customers:objects.actions.reorder')}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" strokeWidth={2} />
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2 text-[13px] font-medium text-slate-900">
          {object.displayOrder && object.displayOrder > 0 && (
            <span className="font-mono-num text-slate-400 font-semibold tabular-nums">
              {object.displayOrder}.
            </span>
          )}
          <span className="truncate">{object.name}</span>
        </div>
        <div className="text-[11px] text-slate-500 sm:hidden mt-0.5 truncate">
          {[object.address?.street, object.address?.city].filter(Boolean).join(', ')}
        </div>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <span className="text-[13px] text-slate-600">
          {object.address?.street}, {object.address?.postalCode} {object.address?.city}
        </span>
      </TableCell>
      <TableCell className="hidden sm:table-cell">
        <Badge variant={object.status === 'active' ? 'success' : 'default'}>
          {object.status === 'active' ? t('common:status.active') : t('common:status.inactive')}
        </Badge>
      </TableCell>
      <TableCell>
        <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 sm:gap-2">
          <Button
            variant="ghost"
            size="sm"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              goToDetail();
            }}
            title={t('customers:objects.actions.view')}
            aria-label={t('customers:objects.actions.view')}
            className="p-1.5"
          >
            <Eye className="w-4 h-4" strokeWidth={2} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(object)}
            title={t('customers:objects.actions.edit')}
            aria-label={t('customers:objects.actions.edit')}
            className="p-1.5"
          >
            <Pencil className="w-4 h-4" strokeWidth={2} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => object.id && onDelete(object.id)}
            title={t('customers:objects.actions.delete')}
            aria-label={t('customers:objects.actions.delete')}
            className="p-1.5"
          >
            <Trash2 className="w-4 h-4 text-red-600" strokeWidth={2} />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

interface DraggableObjectsTableProps {
  objects: FacilityFlowCoreModulesCustomersDtosObjectDto[];
  onReorder: (reorderedObjects: FacilityFlowCoreModulesCustomersDtosObjectDto[]) => void;
  onEdit: (object: FacilityFlowCoreModulesCustomersDtosObjectDto) => void;
  onDelete: (id: string) => void;
}

export function DraggableObjectsTable({ objects, onReorder, onEdit, onDelete }: DraggableObjectsTableProps) {
  const { t } = useTranslation('customers');
  const [localObjects, setLocalObjects] = useState(objects);

  // Update local state when props change
  if (objects !== localObjects && JSON.stringify(objects) !== JSON.stringify(localObjects)) {
    setLocalObjects(objects);
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = localObjects.findIndex(obj => obj.id === active.id);
    const newIndex = localObjects.findIndex(obj => obj.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Reorder array
    const reorderedObjects = [...localObjects];
    const [movedItem] = reorderedObjects.splice(oldIndex, 1);
    reorderedObjects.splice(newIndex, 0, movedItem);

    // Update displayOrder for all items
    const updatedObjects = reorderedObjects.map((obj, index) => ({
      ...obj,
      displayOrder: index + 1
    }));

    setLocalObjects(updatedObjects);
    onReorder(updatedObjects);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader className="w-10 hidden sm:table-cell">{''}</TableHeader>
            <TableHeader>{t('objects.table.name')}</TableHeader>
            <TableHeader className="hidden md:table-cell">{t('objects.table.address')}</TableHeader>
            <TableHeader className="hidden sm:table-cell">{t('objects.table.status')}</TableHeader>
            <TableHeader>{t('objects.table.actions')}</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          <SortableContext
            items={localObjects.map(obj => obj.id || '')}
            strategy={verticalListSortingStrategy}
          >
            {localObjects.map((object) => (
              <SortableObjectRow
                key={object.id}
                object={object}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </SortableContext>
        </TableBody>
      </Table>
    </DndContext>
  );
}
